import { zodResolver } from '@hookform/resolvers/zod';
import { addDays, isAfter, isValid, startOfDay, subDays } from 'date-fns';
import { Snowflake, CheckCircle, Info, CalendarDays, Calculator, PiggyBank } from 'lucide-react';
import { useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import hivernage from '@/assets/images/hivernage.png';
import SimpleDatePicker from '@/components/forms/simple-date-picker';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useActivateHivernage, useEndHivernage, useHivernageReduction } from '@/hooks/api/use-amendments';
import { getActionBlockedReason, getDernierHivernageContract } from '@/lib/contracts';
import { DATE_ISO, formatDate, toDate } from '@/lib/dates';
import { hivernageSchema, type HivernageValues } from '@/schemas/hivernage';
import { useUiStore } from '@/stores/ui-store';
import type { Contract, Hivernage as HivernageContrat } from '@/types/api/contract';

import ClausesHivernage from '@/pages/contract/hivernage/clauses-hivernage';
import ContrepartiesHivernage from '@/pages/contract/hivernage/contreparties-hivernage';

/**
 * Date de fin maximale de Ma période hivernale : 120 jours (moins ceux déjà consommés), sans
 * dépasser le 1er mars suivant ni la veille de l'échéance (sauf échéance aujourd'hui).
 */
export const getMaxDate = (dernier_hivernage: HivernageContrat | null, echeance?: string) => {
    let maxDate = addDays(new Date(), 120);
    let maxDateEcheance = false;
    let finHivernagePeriode = new Date(new Date().getFullYear(), 2, 1);

    if(isAfter(startOfDay(new Date()), finHivernagePeriode)){
        finHivernagePeriode = new Date(finHivernagePeriode.getFullYear() + 1, 2, 1);
    }

    if(dernier_hivernage && dernier_hivernage?.periodeCourante && dernier_hivernage.statutCode !== '1'){
        // si on a déjà eu une période hivernale terminée
        maxDate = addDays(new Date(), 120 - (dernier_hivernage.duree ?? NaN));
    }
    if(isAfter(maxDate, finHivernagePeriode)){
        maxDate = finHivernagePeriode;
    }
    const dateEcheance = toDate(echeance);
    /* Échéance inconnue : pas de borne, plutôt qu'une échéance « aujourd'hui » qui fermerait l'hivernage. */
    if(isValid(dateEcheance) && dateEcheance.getTime() !== startOfDay(new Date()).getTime() && isAfter(maxDate, subDays(dateEcheance, 1))){
        //La date max ne peut pas dépasser le terme sauf si le terme est aujourd'hui
        maxDate = subDays(dateEcheance, 1)
        maxDateEcheance = true;
    }

    return { maxDate, maxDateEcheance };
};

export interface HivernageProps {
    contract: Contract;
}

const Hivernage = ({ contract }: HivernageProps) => {
    const id_contrat = contract.id;
    const dernier_hivernage = getDernierHivernageContract(contract);
    const { maxDate, maxDateEcheance } = getMaxDate(dernier_hivernage, contract.situation?.dateEcheance);
    const is_eligible_hivernage = contract.is_eligible_hivernage || false;
    const motifBlocage = getActionBlockedReason(contract, 'hivernage');
    const initialValues: HivernageValues = {
        dateFin: formatDate(maxDate, DATE_ISO),
    };
    const { mutate: postHivernage, isPending: loading } = useActivateHivernage();
    const { mutate: postFinHivernage, isPending: loading_fin } = useEndHivernage();
    const reductionMutation = useHivernageReduction();
    const getHivernageReduc = reductionMutation.mutate;
    const loading_get_reduc = reductionMutation.isPending;
    /* Ancien `loading === false` : vrai une fois l'estimation revenue, en succès comme en échec. */
    const reductionLoaded = reductionMutation.isSuccess || reductionMutation.isError;
    const hivernage_reduction = useUiStore((state) => state.hivernageReduction);
    const isActive = dernier_hivernage?.periodeCourante && dernier_hivernage.statutCode === '1';
    const isTerminee = dernier_hivernage?.periodeCourante && dernier_hivernage.statutCode !== '1';
    /* Une période en cours rend l'action disponible pour la terminer, pas pour en ouvrir une autre */
    const can_activate_hivernage = !!contract.can_activate_hivernage && !isActive;
    /* Date de fin pour laquelle la réduction a été estimée : l'activation n'est possible que si elle correspond à la date saisie */
    const [dateEstimee, setDateEstimee] = useState<string | null>(null)
    const navigate = useNavigate();

    const methods = useForm<HivernageValues>({
        defaultValues: initialValues,
        resolver: zodResolver(hivernageSchema) as unknown as Resolver<HivernageValues>,
        mode: 'onSubmit',
    });

    const { handleSubmit, formState: { errors }, watch, setValue } = methods;
    const values = watch();
    const dateFin = values.dateFin ? formatDate(values.dateFin, DATE_ISO) : null;
    const tarifSimule = !!hivernage_reduction && reductionLoaded && dateEstimee === dateFin;

    if (!is_eligible_hivernage){
        return <Navigate to={`/mes-contrats/mon-contrat/${id_contrat}`} />
    }

    const handleEstimer = () => {
        setDateEstimee(dateFin)
        getHivernageReduc({contractId: id_contrat, form: values})
    }

    const handleFinHivernage = () => {
        Swal.fire({
            title: 'Confirmation',
            text: 'En désactivant de manière anticipée Ma période hivernale, il ne sera plus possible de la réactiver.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Oui, je désactive',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                postFinHivernage({contractId: id_contrat})
            }
        });
    }

    return (
        <div className="space-y-6">
            {/* Hero */}
            <div className="relative overflow-hidden rounded-2xl bg-primary p-6 sm:p-8 text-white mt-12">
                <div className="absolute -top-1/2 -right-[10%] w-[400px] h-[400px] bg-white/10 rounded-full" />
                <div className="relative z-10">
                    <p className="text-sm opacity-90 mb-1">Mon contrat</p>
                    <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-3">
                        <Snowflake className="w-7 h-7 sm:w-8 sm:h-8" />
                        Ma période hivernale
                    </h1>
                    <p className="text-sm opacity-80 mt-2">Vous n'utilisez plus votre moto en hiver ? Bénéficiez d'une réduction sur votre cotisation.</p>
                </div>
            </div>

            {/* Statut de la période hivernale */}
            {isActive && (
                <Alert variant="success">
                    <CheckCircle className="w-4 h-4" />
                    <AlertTitle>Ma période hivernale est activée</AlertTitle>
                    <AlertDescription>
                        <p>Elle est active jusqu'au <span className="font-semibold">{formatDate(dernier_hivernage.dateFin)}</span>.</p>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="mt-4 cursor-pointer"
                            onClick={handleFinHivernage}
                            loading={loading_fin}
                            loadingText="Désactivation en cours..."
                        >
                            Je désactive Ma période hivernale de manière anticipée
                        </Button>
                    </AlertDescription>
                </Alert>
            )}

            {isTerminee && (
                <Alert variant="warning">
                    <Info className="w-4 h-4" />
                    <AlertDescription>
                        <p>
                            Vous avez bénéficié de Ma période hivernale du <span className="font-semibold">{formatDate(dernier_hivernage.dateDebut)}</span> au <span className="font-semibold">{formatDate(dernier_hivernage.dateFin)}</span>.
                        </p>
                        {!can_activate_hivernage && <p className="font-semibold">Il n'est plus possible de l'activer.</p>}
                    </AlertDescription>
                </Alert>
            )}

            {!isActive && !isTerminee && motifBlocage && (
                <Alert variant="warning">
                    <Info className="w-4 h-4" />
                    <AlertTitle>Ma période hivernale n'est pas activable sur ce contrat</AlertTitle>
                    <AlertDescription>
                        <p>{motifBlocage}</p>
                    </AlertDescription>
                </Alert>
            )}

            {/* Fonctionnement */}
            <Card>
                <CardHeader className="pb-6 border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Snowflake className="w-5 h-5 text-primary" />
                        Comment ça marche ?
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                        <div className="flex-1">
                            <ClausesHivernage />
                        </div>
                        <div className="hidden md:flex shrink-0 items-center justify-center">
                            <img src={hivernage} alt="" className="w-48" />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Contreparties */}
            <ContrepartiesHivernage />

            {/* Activation */}
            {can_activate_hivernage && (
                <form onSubmit={handleSubmit(values => postHivernage({contractId: id_contrat, form: values, successFunction: () => navigate(`/mes-contrats/mon-contrat/${id_contrat}`)}))}>
                    <Card>
                        <CardHeader className="pb-6 border-b">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <CalendarDays className="w-5 h-5 text-primary" />
                                {isTerminee ? 'Réactiver Ma période hivernale' : 'Activer Ma période hivernale'}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6 space-y-6">
                            <p className="text-sm text-muted-foreground">
                                La date de début est <span className="font-semibold text-foreground">obligatoirement celle d'aujourd'hui</span> : choisissez uniquement la date de fin.
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div>
                                    <p className="text-sm text-muted-foreground mb-0.5">Date de début</p>
                                    <p className="text-lg font-semibold">{formatDate(new Date())}</p>
                                </div>
                                <div>
                                    <SimpleDatePicker
                                        label={'Date de fin'}
                                        name={'dateFin'}
                                        setValue={(name, value) => setValue(name as 'dateFin', value)}
                                        value={dateFin}
                                        error={errors.dateFin?.message}
                                        required
                                        min={formatDate(addDays(new Date(), 1), DATE_ISO)}
                                        maxDate={new Date(formatDate(maxDate, DATE_ISO))}
                                    />
                                    <p className="text-xs text-muted-foreground mt-2">
                                        {maxDateEcheance
                                            ? <>Elle ne peut pas dépasser la date d'échéance : <span className="font-semibold">{formatDate(maxDate)}</span></>
                                            : <>Elle ne peut pas dépasser le <span className="font-semibold">{formatDate(maxDate)}</span></>
                                        }
                                    </p>
                                </div>
                            </div>

                            <Button
                                type="button"
                                variant="outline"
                                className="w-full sm:w-auto cursor-pointer"
                                onClick={handleEstimer}
                                disabled={!dateFin}
                                loading={loading_get_reduc}
                                loadingText="Estimation en cours..."
                            >
                                <Calculator className="w-4 h-4" />
                                Estimer ma réduction
                            </Button>

                            {tarifSimule && (
                                <Alert variant="success">
                                    <PiggyBank className="w-4 h-4" />
                                    <AlertTitle>Réduction estimée : {String(hivernage_reduction)} €</AlertTitle>
                                    <AlertDescription>
                                        Pour Ma période hivernale du {formatDate(new Date())} au {formatDate(dateFin)}. Le montant exact de la réduction sera affiché sur la lettre&#8209;avenant transmise après activation.
                                    </AlertDescription>
                                </Alert>
                            )}

                            <div className="pt-2 border-t">
                                <Button
                                    type="submit"
                                    className="w-full mt-6 cursor-pointer text-white"
                                    disabled={!tarifSimule}
                                    loading={loading}
                                    loadingText="Activation en cours..."
                                >
                                    <Snowflake className="w-4 h-4" />
                                    {isTerminee
                                        ? 'Je réactive Ma période hivernale aux dates choisies'
                                        : 'J\'active Ma période hivernale aux dates choisies'}
                                </Button>
                                {!tarifSimule && (
                                    <p className="text-xs text-muted-foreground text-center mt-2">Estimez votre réduction pour pouvoir activer Ma période hivernale.</p>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </form>
            )}
        </div>
    );
};

export default Hivernage;
